/**
 * Drag-path diagnostics for the 2D designer, printed to the console.
 *
 * Turn on with `?perf=1` in the URL, or `localStorage.setItem('cctvPerf','1')`
 * and reload. Off by default and every call site is behind a single boolean, so
 * production pays one property read per stage.
 *
 * What it prints:
 *   - every long task (>50 ms) with its start offset, as the browser sees it
 *   - every INP-eligible interaction split into input-delay / processing /
 *     presentation, which is the split that says whether a slow drag is the
 *     handler's fault or a thread that was already busy
 *   - per drag-move stage timings and counters, summarised when the drag ends
 *   - the apex-inside-obstacle state, since that is the reported trigger
 *
 * Paste the whole console block; the summary line names the dominant stage.
 */

const FLAG = 'cctvPerf';

function resolveEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const w = window as unknown as { __PERF__?: boolean };
    if (w.__PERF__ === true) return true;
    // Query OR hash, because a router that normalises the search string would
    // otherwise silently drop the flag before anything could read it. The hash
    // check requires an explicit perf=1/perf=true — it used to be a bare
    // substring match, which would have PERMANENTLY enabled diagnostics (the
    // flag persists to localStorage below) for any visitor landing on a URL
    // whose hash merely contained "perf", e.g. a future #performance anchor.
    const q = new URLSearchParams(window.location.search).get('perf');
    const h = /[#&?]perf=(1|true)\b/.test(window.location.hash);
    if (q === '1' || q === 'true' || h) {
      window.localStorage.setItem(FLAG, '1');
      return true;
    }
    if (q === '0' || q === 'false') {
      window.localStorage.removeItem(FLAG);
      return false;
    }
    return window.localStorage.getItem(FLAG) === '1';
  } catch {
    return false;
  }
}

export const PERF = resolveEnabled();

if (typeof window !== 'undefined') {
  (window as unknown as { cctvPerf: (on?: boolean) => void }).cctvPerf = (on = true) => {
    if (on) window.localStorage.setItem(FLAG, '1');
    else window.localStorage.removeItem(FLAG);
    window.location.reload();
  };
  // Printed whether on or off: "no [perf] lines" must never be ambiguous
  // between "the flag is off" and "the instrumentation is broken".
  // eslint-disable-next-line no-console
  console.log(
    `%c[perf] flaga = ${PERF ? 'WLACZONA' : 'WYLACZONA'}` +
    (PERF ? '' : '  → wpisz  cctvPerf()  w konsoli i odswiez, albo dodaj ?perf=1'),
    `color:${PERF ? '#0f0' : '#f80'};font-weight:bold`,
  );
}

// ─── accumulators ───────────────────────────────────────────────────────────

interface Stage { calls: number; ms: number; max: number }
const stages = new Map<string, Stage>();
const counters = new Map<string, number>();
const notes: string[] = [];

// Console/automation reader for the session counters (render counts, cache
// hits…): the gesture report only prints them for DRAGS, and asserting
// "this gesture rendered N components" needs a before/after diff, not a
// pretty table. PERF-gated like every other surface of this module.
//
// __perfTally lives on WINDOW, written by pcount itself, and is never
// cleared by the gesture reports — two properties that make it readable
// from automation regardless of dev-server module-graph duplication (a
// Vite HMR session can serve ?t= and bare instances of this module whose
// private Maps are disjoint; the window object is shared by all of them).
if (PERF && typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__perfCounters =
    () => Object.fromEntries(counters);
}

let dragActive = false;
let dragMoves = 0;
let dragStartedAt = 0;
let dragLabel = '';
let moveStartedAt = 0;
let worstMove = 0;
let worstMoveNote = '';

function bump(map: Map<string, Stage>, key: string, ms: number) {
  const s = map.get(key);
  if (s) {
    s.calls++;
    s.ms += ms;
    if (ms > s.max) s.max = ms;
  } else {
    map.set(key, { calls: 1, ms, max: ms });
  }
}

/** Time a stage. Returns the callee's value; adds nothing when PERF is off. */
export function pstage<T>(label: string, fn: () => T): T {
  if (!PERF) return fn();
  const t0 = performance.now();
  try {
    return fn();
  } finally {
    bump(stages, label, performance.now() - t0);
  }
}

/** Summed milliseconds of the given stages so far. Snapshot before and after
 *  a commit to attribute its wall time WITHOUT double counting nested stages
 *  (callers pass only top-level labels). */
export function pstageMs(labels: string[]): number {
  let total = 0;
  for (const l of labels) total += stages.get(l)?.ms ?? 0;
  return total;
}

/** Summed counter values, same snapshot idiom (e.g. cache HIT/MISS per commit). */
export function pcounts(labels: string[]): number {
  let total = 0;
  for (const l of labels) total += counters.get(l) ?? 0;
  return total;
}

/** TOP-LEVEL geometry stages only: classify already contains the shadow clip,
 *  the footprint contains the terrain march. Listing nested stages here would
 *  count the same milliseconds twice — the exact bookkeeping error the
 *  architecture review caught in two of three rewrite proposals. */
export const GEOMETRY_STAGE_LABELS = [
  'classifyCameraBlockers',
  'occludeFootprint',
  'computeGroundFootprint',
];

/**
 * Capture the EXACT inputs of a slow call so the pathology can be replayed
 * offline byte-for-byte instead of guessed at. Keeps the entry on
 * `window.__slowClips` (capped at 5 per session); on a dev host it is also
 * POSTed to the vite /__perf-dump endpoint, which writes it to /tmp.
 *
 * NEVER triggers a browser download. An earlier version fired `a.click()` as
 * a non-dev fallback, and because `?perf=1` persists the flag into
 * localStorage, the owner's PRODUCTION origin (flag left over from the
 * 08-17 freeze debugging) started downloading `cctv-slow-clip-0ms.json` on
 * every project load — the empty-coverage tripwire fires for cameras placed
 * inside buildings, which is a data condition, not a pathology. Files
 * appearing in a customer's Downloads folder is never an acceptable
 * diagnostic channel; the console snippet below is the manual retrieval path.
 */
export function pcapture(label: string, dt: number, payload: unknown): void {
  if (!PERF || typeof window === 'undefined') return;
  const w = window as unknown as { __slowClips?: unknown[] };
  w.__slowClips = w.__slowClips ?? [];
  if (w.__slowClips.length >= 5) return;
  const entry = { label, dt: Math.round(dt), at: new Date().toISOString(), payload };
  w.__slowClips.push(entry);
  let json = '';
  try { json = JSON.stringify(entry); } catch { return; }
  // Dev server saves the dump to /tmp so nobody has to click anything.
  //
  // DEV ONLY: on production the endpoint does not exist and Apache's SPA
  // fallback answers 200 with the 404 PAGE, so an unguarded .text() log dumped
  // entire HTML documents into the console. Localhost check first, and even
  // there only log a response that looks like the file path the plugin returns.
  const isDevHost = /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
  if (isDevHost) {
    try {
      fetch('/__perf-dump', { method: 'POST', headers: { 'content-type': 'application/json' }, body: json, keepalive: true })
        .then((r) => r.text())
        .then((f) => {
          // eslint-disable-next-line no-console
          if (f.startsWith('/')) console.log(`%c[perf] zrzut zapisany na dysku: ${f}`, 'color:#0f0;font-weight:bold');
        })
        .catch(() => { /* endpoint absent — window.__slowClips still holds it */ });
    } catch { /* fetch unavailable */ }
  }
  /* eslint-disable no-console */
  console.log(
    `%c[perf] SLOW ${label}: ${dt.toFixed(0)} ms — wejscia w window.__slowClips`,
    'color:#f00;font-weight:bold',
  );
  console.log(
    "zapis do pliku recznie: (()=>{const a=document.createElement('a');" +
    "a.href=URL.createObjectURL(new Blob([JSON.stringify(window.__slowClips)],{type:'application/json'}));" +
    "a.download='cctv-slow-clip.json';a.click();})()",
  );
  /* eslint-enable no-console */
}

/** Record a duration measured by the caller (React commit, etc.). */
export function pmeasure(label: string, ms: number): void {
  if (!PERF) return;
  bump(stages, label, ms);
}

/** Is a drag/zoom session already open? Lives here so a React effect that
 *  re-creates its handlers cannot reset the session and restart the report. */
export function perfSessionOpen(): boolean {
  return PERF && dragActive;
}

/** Count an event (cache hit, fallback step taken, render, …). */
export function pcount(label: string, by = 1): void {
  if (!PERF) return;
  counters.set(label, (counters.get(label) ?? 0) + by);
  if (typeof window !== 'undefined') {
    const t = ((window as unknown as Record<string, unknown>).__perfTally ??= {}) as Record<string, number>;
    t[label] = (t[label] ?? 0) + by;
  }
}

/** Free-text observation attached to the current drag report. */
export function pnote(text: string): void {
  if (!PERF) return;
  if (notes.length < 40 && !notes.includes(text)) notes.push(text);
}

// ─── drag session ───────────────────────────────────────────────────────────

/**
 * Same instrumentation for a non-drag burst: a zoom/pan gesture. Reported the
 * moment it goes quiet, because a wheel zoom has no release event to hang the
 * report on — `zoomTick` restarts a short idle timer and the report fires when
 * the ticks stop.
 */
let zoomTimer: ReturnType<typeof setTimeout> | undefined;
let zoomTicks = 0;
let zoomTickStart = 0;

export function zoomTick(label: string): void {
  if (!PERF) return;
  if (!dragActive) {
    dragBegin(label);
    zoomTicks = 0;
  }
  // dragBegin resets the clock, so restore the moment this tick actually began
  // or the first tick of every gesture measures from the previous gesture.
  moveStartedAt = zoomTickStart || performance.now();
  zoomTicks++;
  moveEnd(`tick ${zoomTicks}`);
  if (zoomTimer) clearTimeout(zoomTimer);
  zoomTimer = setTimeout(() => {
    zoomTimer = undefined;
    dragEnd();
  }, 600);
}

export function zoomTickBegin(): void {
  if (!PERF) return;
  zoomTickStart = performance.now();
  moveBegin();
}

export function dragBegin(label: string): void {
  if (!PERF) return;
  stages.clear();
  counters.clear();
  notes.length = 0;
  dragActive = true;
  dragMoves = 0;
  worstMove = 0;
  worstMoveNote = '';
  dragLabel = label;
  dragStartedAt = performance.now();
  // eslint-disable-next-line no-console
  console.log(`%c[perf] DRAG START — ${label}`, 'color:#0af;font-weight:bold');
}

export function moveBegin(): void {
  if (!PERF || !dragActive) return;
  moveStartedAt = performance.now();
}

export function moveEnd(note = ''): void {
  if (!PERF || !dragActive) return;
  const dt = performance.now() - moveStartedAt;
  dragMoves++;
  bump(stages, 'TOTAL handler', dt);
  if (dt > worstMove) {
    worstMove = dt;
    worstMoveNote = note;
  }
}

export function dragEnd(): void {
  if (!PERF || !dragActive) return;
  dragActive = false;
  const wall = performance.now() - dragStartedAt;
  const rows = [...stages.entries()]
    .map(([k, s]) => ({
      etap: k,
      wywołań: s.calls,
      'suma ms': +s.ms.toFixed(1),
      'śr ms': +(s.ms / s.calls).toFixed(2),
      'max ms': +s.max.toFixed(1),
      '% gestu': +((s.ms / wall) * 100).toFixed(1),
    }))
    .sort((a, b) => b['suma ms'] - a['suma ms']);

  /* eslint-disable no-console */
  console.group(
    `%c[perf] DRAG END — ${dragLabel} — ${dragMoves} ruchów w ${wall.toFixed(0)} ms, ` +
    `najgorszy ruch ${worstMove.toFixed(0)} ms${worstMoveNote ? ` (${worstMoveNote})` : ''}`,
    'color:#0af;font-weight:bold',
  );
  if (rows.length) console.table(rows);
  if (counters.size) {
    console.table(
      [...counters.entries()]
        .map(([k, v]) => ({ licznik: k, ile: v, 'na ruch': +(v / Math.max(1, dragMoves)).toFixed(2) }))
        .sort((a, b) => b.ile - a.ile),
    );
  }
  if (notes.length) console.log('uwagi:', notes.join(' | '));
  const accounted = rows.find((r) => r.etap === 'TOTAL handler')?.['suma ms'] ?? 0;
  console.log(
    `%cwątek: ${wall.toFixed(0)} ms gestu, ${accounted.toFixed(0)} ms w handlerach ` +
    `(${((accounted / wall) * 100).toFixed(0)}%). Resztę zjadło coś POZA handlerem.`,
    'color:#f80',
  );
  console.groupEnd();
  /* eslint-enable no-console */
}

// ─── full-state snapshot support ────────────────────────────────────────────

/** Ring buffer of the last JS errors/rejections, included in state dumps. */
const errorRing: { at: number; msg: string }[] = [];
export function psnapshot(): {
  stages: Record<string, { calls: number; ms: number; max: number }>;
  counters: Record<string, number>;
  errors: { at: number; msg: string }[];
} {
  const st: Record<string, { calls: number; ms: number; max: number }> = {};
  for (const [k, v] of stages) st[k] = { ...v };
  const ct: Record<string, number> = {};
  for (const [k, v] of counters) ct[k] = v;
  return { stages: st, counters: ct, errors: [...errorRing] };
}

// ─── observers: long tasks + interaction phases ─────────────────────────────

let observersOn = false;

export function startPerfObservers(): void {
  if (!PERF || observersOn || typeof PerformanceObserver === 'undefined') return;
  observersOn = true;
  window.addEventListener('error', (e) => {
    errorRing.push({ at: Math.round(performance.now()), msg: String(e.message || e.error || 'error') });
    if (errorRing.length > 25) errorRing.shift();
  });
  window.addEventListener('unhandledrejection', (e) => {
    errorRing.push({ at: Math.round(performance.now()), msg: `rejection: ${String(e.reason).slice(0, 300)}` });
    if (errorRing.length > 25) errorRing.shift();
  });
  /* eslint-disable no-console */
  console.log(
    '%c[perf] diagnostyka WŁĄCZONA. Wyłącz: ?perf=0 albo localStorage.removeItem(\'cctvPerf\')',
    'color:#0f0;font-weight:bold',
  );

  try {
    new PerformanceObserver((list) => {
      for (const e of list.getEntries()) {
        const attr = (e as PerformanceEntry & { attribution?: { name: string; containerName?: string }[] }).attribution;
        console.log(
          `%c[perf] LONG TASK ${e.duration.toFixed(0)} ms @ ${e.startTime.toFixed(0)} ms` +
          (attr?.length ? ` — ${attr.map((a) => a.containerName || a.name).join(',')}` : ''),
          'color:#f44',
        );
      }
    }).observe({ type: 'longtask', buffered: true });
  } catch { /* longtask unsupported */ }

  try {
    new PerformanceObserver((list) => {
      for (const raw of list.getEntries()) {
        const e = raw as PerformanceEventTiming;
        if (e.duration < 100) continue;
        const inputDelay = e.processingStart - e.startTime;
        const processing = e.processingEnd - e.processingStart;
        const presentation = e.startTime + e.duration - e.processingEnd;
        console.log(
          `%c[perf] ${e.name} ${e.duration.toFixed(0)} ms na <${(e.target as Element | null)?.tagName?.toLowerCase() ?? '?'}` +
          `${(e.target as Element | null)?.className ? '.' + String((e.target as Element).className).split(' ')[0] : ''}> ` +
          `→ wejście ${inputDelay.toFixed(0)} / handler ${processing.toFixed(0)} / malowanie ${presentation.toFixed(0)}`,
          'color:#f0f;font-weight:bold',
        );
      }
    }).observe({ type: 'event', durationThreshold: 100, buffered: true } as PerformanceObserverInit);
  } catch { /* event timing unsupported */ }
  /* eslint-enable no-console */
}

/**
 * React's own render cost for a subtree, which no handler timer can see because
 * `setState` schedules the render as its own task. `actualDuration` is what
 * React spent rendering the components inside; zero when memo let it bail out.
 */
export function onPerfRender(id: string, _phase: string, actualDuration: number): void {
  if (!PERF) return;
  bump(stages, `React: ${id}`, actualDuration);
}
